import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageMeta } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { ImageSlot } from "@/components/ImageSlot";
import { PageHero, SectionHeader } from "@/components/Section";
import { Ambient } from "@/components/Ambient";
import { SEO, CULTURE } from "@/content/siteContent";

const EditorialBlock = ({ eyebrow, title, body, slotKey, flip = false, dark = false, aspect = "16 / 10" }) => (
    <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
        <div className={flip ? "lg:order-2" : ""}>
            <SectionHeader eyebrow={eyebrow} title={title} dark={dark} />
            <Reveal delay={0.1}>
                <p className={`body-copy mt-7 max-w-xl ${dark ? "text-[#FFF5E6]/75" : ""}`}>{body}</p>
            </Reveal>
        </div>
        <Reveal delay={0.15} className={flip ? "lg:order-1" : ""}>
            <ImageSlot slotKey={slotKey} aspect={aspect} light={!dark} />
        </Reveal>
    </div>
);

export default function Culture() {
    return (
        <>
            <PageMeta title={SEO.culture.title} description={SEO.culture.description} />
            <PageHero
                eyebrow="Culture & Community"
                title="A Puja that brings people together"
                intro={CULTURE.intro.body}
            />

            {/* Cultural programming — light editorial */}
            <section className="surface-ivory py-20 sm:py-28" data-testid="culture-artists">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <EditorialBlock
                        eyebrow="Cultural Programmes"
                        title={CULTURE.artists.title}
                        body={CULTURE.artists.body}
                        slotKey="cultural"
                    />
                </div>
            </section>

            {/* Children's activities — dark */}
            <section className="surface-dark relative overflow-hidden py-20 sm:py-28" data-testid="culture-children">
                <Ambient />
                <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
                    <EditorialBlock
                        eyebrow="Children's Competitions"
                        title={CULTURE.children.title}
                        body={CULTURE.children.body}
                        slotKey="children"
                        flip
                        dark
                    />
                </div>
            </section>

            {/* Kumari Puja — light, centred */}
            <section className="surface-ivory py-20 sm:py-28" data-testid="culture-kumari">
                <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
                    <SectionHeader eyebrow="The Night of Worship" title={CULTURE.kumari.title} align="center" />
                    <Reveal delay={0.1}>
                        <p className="body-copy mx-auto mt-7 max-w-2xl">{CULTURE.kumari.body}</p>
                    </Reveal>
                    <Reveal delay={0.2} className="mx-auto mt-12 max-w-2xl">
                        <ImageSlot slotKey="kumariPuja" aspect="16 / 9" light />
                    </Reveal>
                </div>
            </section>

            {/* Puja-season community care — dark */}
            <section className="surface-dark relative overflow-hidden py-20 sm:py-28" data-testid="culture-care">
                <Ambient />
                <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
                    <EditorialBlock
                        eyebrow="During the Puja"
                        title={CULTURE.care.title}
                        body={CULTURE.care.body}
                        slotKey="community"
                        dark
                    />
                </div>
            </section>

            {/* Agnikar — maroon feature */}
            <section className="surface-maroon relative overflow-hidden py-20 sm:py-28" data-testid="culture-agnikar">
                <Ambient />
                <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
                    <EditorialBlock
                        eyebrow="Agnikar"
                        title={CULTURE.agnikar.title}
                        body={CULTURE.agnikar.body}
                        slotKey="agnikar"
                        flip
                        dark
                    />
                </div>
            </section>

            <section className="surface-ivory py-16 sm:py-20" data-testid="culture-cta">
                <div className="mx-auto flex max-w-4xl flex-col items-center gap-6 px-5 text-center sm:px-8">
                    <Reveal>
                        <h2 className="font-display text-3xl font-semibold text-[#260910] sm:text-4xl">
                            Perform, participate or simply be there
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1} className="flex flex-wrap justify-center gap-4">
                        <Link to="/contact" className="btn btn-outline--dark" data-testid="culture-contact-cta">
                            Contact the Trust <ArrowRight size={15} />
                        </Link>
                        <Link to="/contact" className="btn btn-outline--dark" data-testid="culture-programme-cta">
                            Cultural Programme Enquiries <ArrowRight size={15} />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
